import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, combineLatestWith, distinctUntilChanged, map, retry, shareReplay, switchMap, tap } from 'rxjs/operators';
import { clearBrowserCachesAndReload } from 'src/app/shared/utils/clear-browser-caches';
import { AppRelease } from 'src/app/utils/cargo-driver-check-in-model/app-release';
import { VersionMetadata } from 'src/app/utils/cargo-driver-check-in-model/version-metadata';
import { AppReleaseService } from 'src/app/utils/cargo-driver-check-in-service/services/app-release.service';
import { environment } from 'src/environments/environment';
import { isNewerVersion } from '../utils/semver';
import { VersionRefreshGuard } from '../utils/version-refresh-guard';

@Injectable({ providedIn: 'root' })
export class VersionService {

    private readonly currentVersion: string = environment.appVersion;
    private versionMetadata = new BehaviorSubject<VersionMetadata | null>(null);
    private reloadRequestedByServer = new BehaviorSubject<boolean>(false);
    private refreshGuard = new VersionRefreshGuard();
    private listeningStream: Observable<string> | null = null;

    get instantReloadCandidate(): boolean {
        return this.calculateIsReloadCandidate({
            metadata: this.versionMetadata.getValue(),
            reloadRequestedByServer: this.reloadRequestedByServer.getValue()
        });
    }

    get instantShouldReloadNow(): boolean {
        return this.calculateShouldReloadNow({
            reloadCandidate: this.instantReloadCandidate,
            metadata: this.versionMetadata.getValue(),
            reloadRequestedByServer: this.reloadRequestedByServer.getValue()
        });
    }

    constructor(
        private appReleaseService: AppReleaseService,
        private httpClient: HttpClient
    ) { }

    startListening(): Observable<string> {
        if (!this.listeningStream) {
            this.listeningStream = this.buildListeningStream();
        }
        return this.listeningStream;
    }

    isReloadCandidate(): Observable<boolean> {
        return this.versionMetadata
            .pipe(
                combineLatestWith(this.reloadRequestedByServer),
                map(([metadata, reloadRequestedByServer]) =>
                    this.calculateIsReloadCandidate({ metadata, reloadRequestedByServer })
                ),
                distinctUntilChanged()
            );
    }

    shouldReloadNow(): Observable<boolean> {
        return this.versionMetadata
            .pipe(
                combineLatestWith(this.reloadRequestedByServer),
                map(([metadata, reloadRequestedByServer]) => {
                    const reloadCandidate = this.calculateIsReloadCandidate({ metadata, reloadRequestedByServer });

                    return this.calculateShouldReloadNow({
                        reloadCandidate,
                        metadata,
                        reloadRequestedByServer
                    });
                }),
                distinctUntilChanged()
            );
    }

    getCurrentVersion(): string {
        return this.currentVersion;
    }

    forceRefresh(): void {
        this.refreshGuard.save();
        clearBrowserCachesAndReload();
    }

    attemptRefresh(): boolean {
        if (this.refreshGuard.isAlreadyAttempted()) {
            return false;
        }

        this.forceRefresh();
        return true;
    }

    private buildListeningStream(): Observable<string> {
        return this.appReleaseService.findCurrent()
            .pipe(
                switchMap(appRelease =>
                    this.processAppRelease(appRelease)
                        .pipe(
                            catchError(() =>
                                of(null)
                            )
                        )
                ),
                tap(metadata => {
                    this.versionMetadata.next(metadata);
                }),
                map(() =>
                    'COMPLETE'
                ),
                shareReplay({ bufferSize: 1, refCount: true })
            );
    }

    private processAppRelease(appRelease: AppRelease | null): Observable<VersionMetadata | null> {
        if (!appRelease || !appRelease.arl_last_deploy) {
            return of(null);
        }

        this.refreshGuard.trackDeploy(appRelease.arl_last_deploy);
        this.reloadRequestedByServer.next(appRelease.arl_force_refresh);
        return this.fetchVersionJson(appRelease.arl_last_deploy);
    }

    private fetchVersionJson(lastDeploy: number): Observable<VersionMetadata> {
        const url = `/assets/version.json?t=${lastDeploy}`;

        return this.httpClient.get<VersionMetadata>(url)
            .pipe(
                retry({ count: 3, delay: 10_000 })
            );
    }

    private calculateIsReloadCandidate({
        metadata,
        reloadRequestedByServer
    }: {
        metadata: VersionMetadata | null;
        reloadRequestedByServer: boolean;
    }): boolean {
        if (this.refreshGuard.isAlreadyAttempted()) {
            return false;
        }
        if (reloadRequestedByServer) {
            return true;
        }
        if (!metadata) {
            return false;
        }
        return isNewerVersion(metadata.version, this.currentVersion);
    }

    private calculateShouldReloadNow({
        reloadCandidate,
        metadata,
        reloadRequestedByServer
    }: {
        reloadCandidate: boolean;
        metadata: VersionMetadata | null;
        reloadRequestedByServer: boolean;
    }): boolean {
        if (!reloadCandidate) {
            return false;
        }
        if (this.refreshGuard.isAlreadyAttempted()) {
            return false;
        }
        return reloadRequestedByServer || metadata?.severity === 'critical';
    }
}
